/**
 * The intake's supplier picker: type to search, arrow keys to move, Enter to
 * pick, and "Add '{text}' as a new supplier" as the last row.
 *
 * Replaces a plain <select> plus a separate inline-add form. Searches the
 * server (GET /stock/suppliers?q=) as the user types, so a shop with many
 * suppliers is not limited to the list loaded with the screen.
 * GRN/variants spec section 5.1.
 */
import React, {useEffect, useId, useRef, useState} from 'react';
import SupplierRepository, {type Supplier} from '../../../internals/repository/SupplierRepository';
import {comboRows, moveHighlight, type ComboRow} from '../intakeEditor';

const inputCls = 'nl-input';

interface Props {
  shopId: string;
  /** Suppliers already loaded with the screen; shown before any typing. */
  suppliers: Supplier[];
  value: string;
  disabled?: boolean;
  onChange: (supplierId: string) => void;
  /** Creates the supplier and returns it. */
  onCreate: (name: string) => Promise<Supplier | null>;
}

const SupplierCombobox = ({shopId, suppliers, value, disabled, onChange, onCreate}: Props) => {
  const selected = suppliers.find((s) => s.supplierId === value);
  const [text, setText] = useState(selected?.name ?? '');
  const [open, setOpen] = useState(false);
  const [found, setFound] = useState<Supplier[] | null>(null);
  const [highlight, setHighlight] = useState(-1);
  const [creating, setCreating] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Follow the selection when it changes from outside (a loaded document, a
  // preselected supplier).
  useEffect(() => {
    if (!open) setText(selected?.name ?? '');
  }, [selected?.name, open]);

  // Server search, debounced. The loaded list stands in until it answers.
  useEffect(() => {
    if (!open || !shopId) return undefined;
    const q = text.trim();
    if (!q) {
      setFound(null);
      return undefined;
    }
    let live = true;
    const t = setTimeout(() => {
      SupplierRepository.list(shopId, q)
        .then((list) => live && setFound(list))
        .catch(() => live && setFound(null));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [text, open, shopId]);

  // Close when the user clicks elsewhere, restoring the chosen name.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // The server matches by prefix; merge it with the loaded list so a match
  // inside a name ("bev" → "Lanka Bev") still shows.
  const pool = found
    ? [...found, ...suppliers.filter((s) => !found.some((f) => f.supplierId === s.supplierId))]
    : suppliers;
  const rows = comboRows(pool, text);

  const choose = async (row: ComboRow) => {
    if (row.kind === 'supplier') {
      onChange(row.supplier.supplierId);
      setText(row.supplier.name);
      setOpen(false);
      return;
    }
    setCreating(true);
    try {
      const created = await onCreate(row.name);
      if (created) {
        onChange(created.supplierId);
        setText(created.name);
        setOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => moveHighlight(h, e.key === 'ArrowDown' ? 1 : -1, rows.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlight >= 0 && rows[highlight]) choose(rows[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setText(selected?.name ?? '');
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex gap-1">
        <input
          className={inputCls}
          role="combobox"
          aria-controls={listId}
          aria-expanded={open}
          aria-autocomplete="list"
          placeholder="No supplier — type to search"
          value={text}
          disabled={disabled || creating}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onKeyDown={onKeyDown}
        />
        {value && !disabled && (
          <button
            type="button"
            title="Clear supplier"
            onClick={() => {
              onChange('');
              setText('');
            }}
            className="nl-btn nl-btn--secondary"
          >
            ✕
          </button>
        )}
      </div>

      {open && !disabled && rows.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-borderc bg-surface py-1 shadow-lg"
        >
          {rows.map((row, i) => (
            <li
              key={row.kind === 'supplier' ? row.supplier.supplierId : `add:${row.name}`}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(row);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`cursor-pointer px-3 py-2 text-sm ${
                i === highlight ? 'bg-brand-soft text-brand' : 'text-text1'
              } ${row.kind === 'add' ? 'border-t border-borderc font-medium text-brand' : ''}`}
            >
              {row.kind === 'supplier' ? row.supplier.name : `Add '${row.name}' as a new supplier`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SupplierCombobox;
